import { Button, Chip, Modal, Skeleton } from '@heroui/react'

import { errorMessage } from '@/lib/api/api-error'

import {
  useInventoryLocations,
  useItemRecipe,
  useItemStock,
  useRecipesUsing,
  useStockItemsById,
} from '../hooks'
import { ConstructionType, RecipeStatus, StockItemType } from '../types'
import type { Recipe, StockItem } from '../types'
import {
  constructionLabels,
  costMethodLabels,
  isCounted,
  itemStatusLabels,
  itemTypeLabels,
  locationLabel,
  quantity,
  recipeQuantity,
  recipeStatusLabels,
  trackingLabels,
  unitCost,
} from './inventory-format'

interface StockItemDetailDialogProps {
  /** The row that was clicked. `null` closes the dialog. */
  item: StockItem | null
  onOpenChange: (open: boolean) => void
  /** Leaves for the Recipes tab with this item's recipe picked out. */
  onShowRecipe: (item: StockItem) => void
}

/**
 * One stock item: where its stock is, how it is costed, and how it relates to
 * recipes — what goes into it, for an item made from a recipe, and what it goes
 * into, for an ingredient. An item can be both (a house-made syrup is an
 * ingredient made from a recipe), and then both sections show.
 *
 * The row that was clicked is shown at once; only the per-location stock and
 * the recipes are worth waiting for.
 */
export function StockItemDetailDialog({
  item,
  onOpenChange,
  onShowRecipe,
}: StockItemDetailDialogProps) {
  return (
    <Modal isOpen={Boolean(item)} onOpenChange={onOpenChange}>
      <Modal.Backdrop className="overlay-scope" variant="blur">
        <Modal.Container size="lg" scroll="inside" placement="center">
          <Modal.Dialog className="max-w-2xl">
            <Modal.CloseTrigger className="end-3 top-3 size-10" />
            {item && (
              <DetailBody
                item={item}
                onClose={() => onOpenChange(false)}
                // The dialog closes on the way out, so it is not left open behind
                // the Recipes tab for the back button to find.
                onShowRecipe={(target) => {
                  onOpenChange(false)
                  onShowRecipe(target)
                }}
              />
            )}
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  )
}

function DetailBody({
  item,
  onClose,
  onShowRecipe,
}: {
  item: StockItem
  onClose: () => void
  onShowRecipe: (item: StockItem) => void
}) {
  const isComposite = item.constructionType === ConstructionType.Composite
  const isIngredient = item.stockItemType === StockItemType.Ingredient

  return (
    <>
      <Modal.Header className="gap-1 pe-10">
        <span className="flex flex-wrap items-center gap-2">
          <Modal.Heading>{item.name}</Modal.Heading>
          <Chip size="sm" variant="soft" color={item.status === 'ACTIVE' ? 'success' : 'default'}>
            {itemStatusLabels[item.status]}
          </Chip>
        </span>
        <p className="text-muted text-xs">
          {item.sku} · {itemTypeLabels[item.stockItemType]} · measured in {item.unitOfMeasure}
        </p>
      </Modal.Header>

      <Modal.Body className="flex flex-col gap-6">
        <StockSection item={item} />

        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-medium">Details</h3>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Field label="Category" value={item.category?.name ?? null} />
            <Field label="Made how" value={constructionLabels[item.constructionType]} />
            <Field label="Stock tracking" value={trackingLabels[item.trackingType]} />
            <Field label="Cost method" value={costMethodLabels[item.costMethod]} />
            <Field
              label="Average cost"
              value={`${unitCost(item.currentAvgCost)} / ${item.unitOfMeasure}`}
            />
            <Field
              label="Default cost"
              value={`${unitCost(item.defaultUnitCost)} / ${item.unitOfMeasure}`}
            />
            <Field
              label="Reorder level"
              value={`${quantity(item.reorderPoint)} ${item.unitOfMeasure}`}
            />
            <Field
              label="Safety buffer"
              value={`${quantity(item.safetyBuffer)} ${item.unitOfMeasure}`}
            />
          </dl>
        </section>

        {isComposite && <RecipeSection item={item} onShowRecipe={onShowRecipe} />}
        {isIngredient && <UsedInSection item={item} />}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="ghost" onPress={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </>
  )
}

/** Totals first, then the locations that hold any of it. */
function StockSection({ item }: { item: StockItem }) {
  const { stock, isLoading } = useItemStock(isCounted(item) ? item.id : null)
  const { locations } = useInventoryLocations()

  if (!isCounted(item)) {
    return (
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-medium">Stock</h3>
        <p className="text-muted border-border rounded-xl border border-dashed px-3 py-2 text-xs">
          Not counted. This item is listed, but its stock is never deducted or counted.
        </p>
      </section>
    )
  }

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-medium">Stock</h3>

      <dl className="border-border bg-surface grid grid-cols-3 gap-x-6 gap-y-3 rounded-2xl border p-4 text-sm">
        <Field label="On hand" value={quantity(item.onHand)} />
        <Field label="Reserved" value={quantity(item.reserved)} />
        <Field
          label="Available"
          value={quantity(item.available)}
          tone={item.available < 0 ? 'danger' : undefined}
        />
      </dl>

      {isLoading ? (
        <Skeleton className="h-16 w-full rounded-xl" />
      ) : stock && stock.locations.length > 0 ? (
        <ul className="divide-border flex flex-col divide-y text-sm">
          {stock.locations.map((level) => {
            const location = locations.find((candidate) => candidate.id === level.locationId)

            return (
              <li key={level.locationId} className="flex items-center justify-between gap-4 py-2">
                <span className={location ? undefined : 'text-muted'}>
                  {location ? locationLabel(location) : 'Unknown location'}
                </span>
                <span className="text-muted tabular-nums">
                  <span
                    className={`text-foreground font-medium ${level.available < 0 ? 'text-danger' : ''}`}
                  >
                    {quantity(level.available)}
                  </span>{' '}
                  available · {quantity(level.onHand)} on hand
                </span>
              </li>
            )
          })}
        </ul>
      ) : (
        <p className="text-muted text-xs">Not held at any location yet.</p>
      )}
    </section>
  )
}

/**
 * What goes into a COMPOSITE item — shown, not edited.
 *
 * Every COMPOSITE item is created with an empty DRAFT recipe, so "no recipe" is
 * usually that draft. Filling it in, or changing it, happens on the Recipes tab;
 * the button here goes there with this item's recipe picked out.
 */
function RecipeSection({
  item,
  onShowRecipe,
}: {
  item: StockItem
  onShowRecipe: (item: StockItem) => void
}) {
  const { recipe, isLoading, error, retry } = useItemRecipe(item.id)

  const isEmpty = !recipe || recipe.lines.length === 0
  const isArchivedItem = item.status === 'ARCHIVED'

  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Recipe</h3>
        {!isLoading && !error && recipe && (
          <Button
            variant="ghost"
            size="sm"
            className="border-border h-8 rounded-full border px-3 text-xs"
            onPress={() => onShowRecipe(item)}
          >
            Open in Recipes
          </Button>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-20 w-full rounded-xl" />
      ) : error ? (
        <div role="alert" className="flex flex-wrap items-center gap-3 text-xs">
          <span className="text-danger flex-1">
            {errorMessage(error, 'Could not load the recipe.')}
          </span>
          <Button variant="ghost" size="sm" onPress={retry}>
            Try again
          </Button>
        </div>
      ) : isEmpty ? (
        <p className="text-muted border-border rounded-xl border border-dashed px-3 py-2 text-xs">
          {isArchivedItem
            ? 'This item is archived, so it cannot be given a recipe.'
            : recipe
              ? 'The recipe is a draft with no ingredients yet. Add them on the Recipes tab to put it to use.'
              : 'This item has no recipe.'}
        </p>
      ) : (
        <RecipeView recipe={recipe} />
      )}
    </section>
  )
}

/** A recipe's yield and its lines, ingredients by name. */
function RecipeView({ recipe }: { recipe: Recipe }) {
  const ingredients = useStockItemsById(recipe.lines.map((line) => line.itemId))

  return (
    <div className="border-border flex flex-col gap-3 rounded-2xl border p-4">
      <p className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">{recipe.name}</span>
        <Chip
          size="sm"
          variant="soft"
          color={recipe.status === RecipeStatus.Active ? 'success' : 'default'}
        >
          {recipeStatusLabels[recipe.status]}
        </Chip>
        <span className="text-muted text-xs">
          Makes {recipeQuantity(recipe.yieldQty)} {recipe.yieldUnit}
        </span>
      </p>

      <ul className="divide-border flex flex-col divide-y text-sm">
        {recipe.lines.map((line) => {
          const ingredient = ingredients.get(line.itemId)

          return (
            <li key={line.id} className="flex items-center justify-between gap-4 py-2">
              <span className={ingredient ? undefined : 'text-muted'}>
                {ingredient?.name ?? 'Loading…'}
              </span>
              <span className="tabular-nums">
                {recipeQuantity(line.qty)} {line.unitOfMeasure}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** Where an ingredient goes — the recipes that list it, with how much each takes. */
function UsedInSection({ item }: { item: StockItem }) {
  const { recipes, isLoading, error } = useRecipesUsing(item.id)

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-medium">Used in</h3>

      {isLoading ? (
        <Skeleton className="h-16 w-full rounded-xl" />
      ) : error ? (
        <p role="alert" className="text-danger text-xs">
          {errorMessage(error, 'Could not load the recipes that use this item.')}
        </p>
      ) : recipes.length === 0 ? (
        <p className="text-muted border-border rounded-xl border border-dashed px-3 py-2 text-xs">
          Not used in any recipe yet. Add it from the recipe of the item it goes into.
        </p>
      ) : (
        <ul className="divide-border flex flex-col divide-y text-sm">
          {recipes.map((recipe) => {
            const line = recipe.lines.find((candidate) => candidate.itemId === item.id)

            return (
              <li key={recipe.id} className="flex items-center justify-between gap-4 py-2">
                <span className="flex items-center gap-2">
                  {recipe.name}
                  {recipe.status !== RecipeStatus.Active && (
                    <Chip size="sm" variant="soft">
                      {recipeStatusLabels[recipe.status]}
                    </Chip>
                  )}
                </span>
                {line && (
                  <span className="text-muted tabular-nums">
                    {recipeQuantity(line.qty)} {line.unitOfMeasure}
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

function Field({ label, value, tone }: { label: string; value: string | null; tone?: 'danger' }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-muted text-xs">{label}</dt>
      <dd
        className={`font-medium tabular-nums ${tone === 'danger' ? 'text-danger' : ''} ${
          value ? '' : 'text-muted font-normal'
        }`}
      >
        {value || 'None'}
      </dd>
    </div>
  )
}
